/**
 * AirlineSelectionModalForSignup - Simplified airline selector for signup flow
 *
 * Differences from AirlineSelectionModal:
 * - Fetches all airlines from database (not user memberships)
 * - Always single-select (radio buttons only)
 * - No role badges or document counts
 * - Excludes Global airline (assigned automatically)
 * - Simpler, cleaner interface for new users
 */

import React, { useState, useEffect } from 'react';
import { X, Building, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { GLOBAL_AIRLINE_ID } from '../../lib/constants';

// =====================================================================
// Types
// =====================================================================

interface Airline {
  id: string;
  name: string;
  code: string;
  region?: string;
}

interface AirlineSelectionModalForSignupProps {
  onSelect: (airline: Airline) => void;
  onClose: () => void;
  selectedAirlineId?: string;
}

// =====================================================================
// Component
// =====================================================================

export const AirlineSelectionModalForSignup: React.FC<AirlineSelectionModalForSignupProps> = ({
  onSelect,
  onClose,
  selectedAirlineId,
}) => {
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [selectedId, setSelectedId] = useState<string>(selectedAirlineId || '');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all airlines on mount
  useEffect(() => {
    const fetchAirlines = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('airlines')
          .select('id, name, code, region')
          .neq('id', GLOBAL_AIRLINE_ID) // Exclude Global
          .order('name');

        if (fetchError) {
          throw fetchError;
        }

        setAirlines(data || []);
      } catch (err) {
        console.error('[AirlineSelectionModalForSignup] Error fetching airlines:', err);
        setError('Failed to load airlines. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAirlines();
  }, []);

  // ===================================================================
  // Handlers
  // ===================================================================

  const handleSelect = (airlineId: string) => {
    setSelectedId(airlineId);
  };

  const handleConfirm = () => {
    const selected = airlines.find((a) => a.id === selectedId);
    if (selected) {
      onSelect(selected);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selectedId) {
      handleConfirm();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-navy">Select Your Airline</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <p className="mt-1 text-sm text-steel">
            Choose the airline you'd like to join. An administrator will review your request.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-orca-elevate" />
              <span className="ml-3 text-steel">Loading airlines...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="text-orca-elevate hover:underline"
              >
                Retry
              </button>
            </div>
          ) : airlines.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-steel">No airlines available at this time.</p>
              <p className="text-sm text-steel/70 mt-2">Please contact an administrator.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {airlines.map((airline) => {
                const isSelected = airline.id === selectedId;

                return (
                  <button
                    key={airline.id}
                    onClick={() => handleSelect(airline.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'bg-orca-elevate/10 border-orca-elevate'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {/* Radio Button */}
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-orca-elevate bg-white' : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2.5 h-2.5 rounded-full bg-orca-elevate" />
                      )}
                    </div>

                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <Building
                        size={24}
                        className={isSelected ? 'text-orca-elevate' : 'text-steel'}
                      />
                    </div>

                    {/* Airline Info */}
                    <div className="flex-1 text-left">
                      <h3
                        className={`font-semibold ${
                          isSelected ? 'text-navy' : 'text-gray-900'
                        }`}
                      >
                        {airline.name}
                      </h3>
                      <p className="text-sm text-steel">
                        {airline.code}
                        {airline.region && ` • ${airline.region}`}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-steel hover:text-navy hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedId}
              className="px-6 py-2 text-sm font-semibold text-white bg-orca-elevate rounded-lg hover:bg-orca-elevate/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirlineSelectionModalForSignup;
